import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { formatResponseTime } from 'src/shared/util/format-response-time';
import { WindowService } from 'src/shared/providers/window.service';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { DashboardWidgetsQueriesService } from 'src/shared/services/dashboard-widgets-queries.service';
import { MonthAttributionService } from 'src/shared/services/month-attribution.service';
import { OnboardingDummyDataService, StrategySuggestion } from 'src/shared/services/onboarding-dummy-data.service';
import { ConciergeApiService, ConciergeSuggestion } from 'src/shared/services/concierge-api.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import {
  FinanceKpisData,
  FinanceSummaryData,
  FinanceSummaryRow,
  FinanceTrendsData,
  FinanceTrendsMonth,
  MonthAttribution,
  OperationsKpisData,
} from 'src/models/monthly-finance.model';

type ViewMode = 'rolling12' | 'next12' | 'thisYear' | 'lastYear';

@Component({
  templateUrl: './strategy.page.html',
  styleUrls: ['./strategy.page.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StrategyPage implements OnInit {
  readonly windowService = inject(WindowService);
  readonly addressesQueries = inject(AddressesQueriesService);
  readonly widgets = inject(DashboardWidgetsQueriesService);
  private readonly monthAttribution = inject(MonthAttributionService);
  private readonly onboardingDummyData = inject(OnboardingDummyDataService);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Small spinner in the top-right of the desktop-header. Visible only
   * during background refetches triggered by route-reuse reattachment.
   */
  showRefreshSpinner = false;

  /** True after the first `setStoredRoute` resolves — purely for debugging. */
  routeReused = false;

  /**
   * First-load gate. While `false`, the page shows the skeleton (which
   * carries the float-in entrance animation). Flips to `true` after the
   * first widgets query settles and the real content replaces every
   * skeleton in place, instantly and with no animation. Never flips back —
   * background refetches surface via `showRefreshSpinner` instead, so
   * route-reuse reattachment never reshows the skeleton.
   */
  readonly isLoaded = signal<boolean>(false);

  /** True while the about-this-page overlay is up. Until dismissed, the page
   *  renders Palm Springs mock data instead of the (likely-empty) real data. */
  readonly showAboutThisPage = signal<boolean>(false);

  /** Cached once on construction so the chart doesn't reshuffle. */
  private readonly mockData = this.onboardingDummyData.getStrategyMockData();

  private readonly today = new Date();
  private readonly currentYear = this.today.getFullYear();
  private readonly currentMonth = this.today.getMonth();

  readonly monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** Property selector value: stringified address id (empty until addresses load). */
  readonly propertyValue = signal<string>('');
  readonly selectedAddressId = computed<number | null>(() =>
    this.propertyValue() === '' ? null : Number(this.propertyValue()),
  );
  readonly propertyItems = computed(() => {
    const items: { value: string; viewValue: string }[] = [];
    for (const a of this.addressesQueries.addresses() ?? []) {
      items.push({ value: String(a.id), viewValue: a['address_name'] || a.address1 });
    }
    return items;
  });
  readonly selectedPropertyLabel = computed(() => {
    const val = this.propertyValue();
    return this.propertyItems().find(p => p.value === val)?.viewValue ?? '';
  });

  /** Guards the one-time zero-history default below from re-running / overriding the user. */
  private defaultViewResolved = false;

  constructor() {
    // Default to the first property once addresses load. Runs only while no
    // property is selected yet, so it never overrides a user's choice.
    effect(() => {
      if (this.propertyValue() !== '') {return;}
      const first = this.propertyItems()[0];
      if (first) {
        this.propertyValue.set(first.value);
        this.pushWidgetParams();
      }
    });

    // When the selected property has no actual history (e.g. a new property or
    // one with only freshly-imported projections), default to the forward-
    // looking "Next 12 Months" view so those projections are visible. Runs once,
    // only while the user is still on the initial default view.
    effect(() => {
      if (this.defaultViewResolved) {return;}
      if (this.view() !== 'rolling12') { this.defaultViewResolved = true; return; }
      if (this.selectedAddressId() === null) {return;}
      const query = this.widgets.financeWidgetsQuery;
      if (!query.isSuccess() || query.isFetching()) {return;} // wait for settled real data
      this.defaultViewResolved = true;
      if (!this.hasActualHistory()) {
        this.view.set('next12');
        this.pushWidgetParams();
      }
    });

    // Load suggestions on first render and whenever the selected property
    // changes. A null address id (no property resolved yet, or an "All
    // Properties" state) fetches the account-wide list, which the API supports,
    // so the panel never stays silent waiting on a property that may not resolve.
    effect(() => {
      this.loadConciergeSuggestions(this.selectedAddressId());
    });
  }

  /** True when the selected property has any real actual revenue/net in range. */
  private hasActualHistory(): boolean {
    const kpis = this.widgets.kpis();
    if (!kpis) {return false;}
    return (kpis.gross_revenue ?? 0) !== 0 || (kpis.net_operating_income ?? 0) !== 0;
  }

  /** Accrual or cash, shared with the Monthly P&L. The widgets query refetches when it flips. */
  readonly attribution = this.monthAttribution.value;
  /** Actuals follow the reading; projections have no payment day and read the same under both. */
  readonly actualColumnLabel = computed(() => this.attribution() === 'cash' ? 'Actual (cash)' : 'Actual (accrual)');

  onAttributionChange(value: MonthAttribution): void {
    this.attribution.set(value);
  }

  readonly view = signal<ViewMode>('rolling12');
  readonly viewOptions: { value: ViewMode; label: string }[] = [
    { value: 'rolling12', label: 'Rolling 12 Months' },
    { value: 'next12', label: 'Next 12 Months' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'lastYear', label: 'Last Year' },
  ];
  readonly viewItems = this.viewOptions.map(o => ({ value: o.value, viewValue: o.label }));
  readonly currentViewLabel = computed(
    () => this.viewOptions.find(o => o.value === this.view())?.label ?? 'Rolling 12 Months',
  );

  async ngOnInit(): Promise<void> {
    // Hydrate localStorage from the metadata endpoint first so the overlay
    // works for fresh / guest accounts where the auth flow may not have run
    // fetchAndStoreAboutThisPageData() yet. This is a no-op when storage is
    // already populated.
    await this.aboutThisPageService.fetchAndStoreAboutThisPageData();
    this.showAboutThisPage.set(this.aboutThisPageService.checkIfShouldShowAboutThisPage('strategy'));
    await this.loadPage();
  }

  // Bumped on each fetch so an earlier (e.g. transient-null) response can't
  // clobber a newer property-scoped one if they resolve out of order.
  private conciergeRequestSeq = 0;

  // Suggestions are scoped to a property, so each one shows only under the property
  // it's about (the constructor effect reloads this when the picker changes).
  private async loadConciergeSuggestions(addressId: number | null): Promise<void> {
    const seq = ++this.conciergeRequestSeq;
    try {
      const suggestions = await lastValueFrom(this.conciergeApi.getSuggestions(addressId));
      if (seq !== this.conciergeRequestSeq) {return;}
      this.conciergeSuggestions.set(suggestions ?? []);
    } catch {
      // A suggestions fetch failure shouldn't block the P&L page — just show none.
      if (seq !== this.conciergeRequestSeq) {return;}
      this.conciergeSuggestions.set([]);
    }
  }

  async approveSuggestion(suggestion: ConciergeSuggestion): Promise<void> {
    await this.resolveSuggestion(
      suggestion,
      () => lastValueFrom(this.conciergeApi.approveSuggestion(suggestion.id)),
      'Suggestion approved',
    );
  }

  async rejectSuggestion(suggestion: ConciergeSuggestion): Promise<void> {
    await this.resolveSuggestion(
      suggestion,
      () => lastValueFrom(this.conciergeApi.rejectSuggestion(suggestion.id)),
      'Suggestion rejected',
    );
  }

  private async resolveSuggestion(
    suggestion: ConciergeSuggestion,
    action: () => Promise<unknown>,
    successMessage: string,
  ): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    if (this.processingSuggestionId() !== null) {
      return;
    }
    this.processingSuggestionId.set(suggestion.id);
    try {
      await action();
      this.conciergeSuggestions.update((items) => items.filter((item) => item.id !== suggestion.id));
      this.util.showSuccess(successMessage);
    } catch (error: any) {
      this.util.showError(error?.error?.message || error?.message || 'Something went wrong');
    } finally {
      this.processingSuggestionId.set(null);
    }
  }

  /**
   * First-load + reattach orchestrator (mirrors bills-content / schedule).
   *
   * - First call (`ngOnInit`): pushes widget params, awaits the finance
   *   widgets query, then flips `isLoaded` so the real content replaces the
   *   skeleton. On desktop, arms a `setStoredRoute('/overview')` listener
   *   that resolves whenever the user navigates back to this page (route is
   *   restored from cache).
   * - Reattach call (`showSpinner: true`): shows the tiny header spinner,
   *   refetches in the background, re-arms the listener via the recursive
   *   call.
   */
  async loadPage(showSpinner = false, setStoredRoute = true): Promise<void> {
    if (showSpinner) {
      this.showRefreshSpinner = true;
      this.cdr.markForCheck();
    }

    this.pushWidgetParams();
    // Not awaited: the operations tiles fill in on their own and must not hold up the page.
    // refetch() ignores `enabled`, so skip it until a property has resolved.
    if (this.widgets.addressId() !== null) {
      void this.widgets.operationsWidgetsQuery.refetch();
    }
    await this.widgets.financeWidgetsQuery.refetch();

    this.showRefreshSpinner = false;
    this.isLoaded.set(true);
    this.cdr.markForCheck();

    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/overview').then(async () => {
        this.routeReused = true;
        await this.loadPage(true, true);
      });
    }
  }

  dismissAboutThisPage(): void {
    this.aboutThisPageService.dismissAboutThisPage('strategy');
    this.showAboutThisPage.set(false);
  }

  onPropertyChange(value: string): void {
    this.propertyValue.set(value);
    this.pushWidgetParams();
  }
  onViewChange(value: string): void {
    this.view.set(value as ViewMode);
    this.pushWidgetParams();
  }

  private pushWidgetParams(): void {
    const { startDate, endDate, year } = this.viewRange();
    this.widgets.addressId.set(this.selectedAddressId());
    this.widgets.startDate.set(startDate);
    this.widgets.endDate.set(endDate);
    this.widgets.year.set(year);
  }

  private viewRange(): { startDate: string; endDate: string; year: number } {
    const v = this.view();
    let startYear: number, startMonth: number, endYear: number, endMonth: number, year: number;
    if (v === 'rolling12') {
      endYear = this.currentYear;
      endMonth = this.currentMonth + 1;
      const startD = new Date(this.currentYear, this.currentMonth - 11, 1);
      startYear = startD.getFullYear();
      startMonth = startD.getMonth() + 1;
      year = endYear;
    } else if (v === 'next12') {
      // The 12 months starting next month (matches the PDF import window),
      // so freshly-imported projections are visible.
      const startD = new Date(this.currentYear, this.currentMonth + 1, 1);
      startYear = startD.getFullYear();
      startMonth = startD.getMonth() + 1;
      const endD = new Date(this.currentYear, this.currentMonth + 12, 1);
      endYear = endD.getFullYear();
      endMonth = endD.getMonth() + 1;
      year = startYear;
    } else if (v === 'thisYear') {
      startYear = endYear = this.currentYear;
      startMonth = 1; endMonth = 12;
      year = this.currentYear;
    } else {
      startYear = endYear = this.currentYear - 1;
      startMonth = 1; endMonth = 12;
      year = this.currentYear - 1;
    }
    const startDate = `${startYear}-${pad2(startMonth)}-01`;
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    const endDate = `${endYear}-${pad2(endMonth)}-${pad2(lastDay)}`;
    return { startDate, endDate, year };
  }

  // Demo sales account: show the same generated finance mock as the overlay,
  // but without the overlay (the account has real data on other pages).
  private readonly isDemo = this.demoDataService.isDemoAccount();

  // ---- Effective data (mock while overlay is up / demo account, BE otherwise) ----------
  readonly effectiveKpis = computed<FinanceKpisData | null>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockData.kpis : this.widgets.kpis(),
  );
  readonly effectiveTrends = computed<FinanceTrendsData | null>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockData.trends : this.widgets.trends(),
  );
  readonly effectiveSummary = computed<FinanceSummaryData | null>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockData.summary : this.widgets.summary(),
  );
  readonly effectiveOpsKpis = computed<OperationsKpisData | null>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockData.opsKpis : this.widgets.opsKpis(),
  );
  // Suggestions only have a mock source today — show them for the demo account
  // and while the about-this-page overlay is up; otherwise keep the empty state.
  readonly effectiveSuggestions = computed<StrategySuggestion[]>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockData.suggestions : [],
  );

  /** Real, actionable concierge suggestions pushed to this client (title + description). */
  readonly conciergeSuggestions = signal<ConciergeSuggestion[]>([]);

  /** Id of the suggestion whose approve/reject request is in flight (locks its buttons). */
  readonly processingSuggestionId = signal<number | null>(null);

  /**
   * Approve/reject are granted to owner+admin only on the backend (member can list
   * but not act), so hide the controls for members instead of letting them tap into
   * a guaranteed "no permission" error.
   */
  readonly canActOnSuggestions = ['owner', 'admin'].includes(localStorage.getItem('user_role') ?? '');

  /** True when the panel has anything to show — real suggestions or the demo list. */
  readonly hasSuggestions = computed<boolean>(
    () => this.conciergeSuggestions().length > 0 || this.effectiveSuggestions().length > 0,
  );

  // ---- KPI values (cents → dollars) -------------------------------------
  private toDollars(v: number | null | undefined): number {
    return v === null || v === undefined ? 0 : v / 100;
  }
  readonly revenueActual = computed(() => this.toDollars(this.effectiveKpis()?.gross_revenue));
  readonly expensesActual = computed(() => Math.abs(this.toDollars(this.effectiveKpis()?.operating_expenses)));
  readonly netActual = computed(() => this.toDollars(this.effectiveKpis()?.net_operating_income));
  readonly occupancyRate = computed<number | null>(() => this.effectiveKpis()?.occupancy ?? null);
  readonly avgNightlyRate = computed<number | null>(() => {
    const cents = this.effectiveKpis()?.avg_nightly_rate;
    return cents === null || cents === undefined ? null : cents / 100;
  });

  // ---- Operational KPIs -------------------------------------------------
  // Each tile hides on a null value: the backend returns null when there is no
  // data, and — for message response times — when the relevant AI agent is disabled.
  readonly avgStarRating = computed<number | null>(() => this.effectiveOpsKpis()?.avg_star_rating ?? null);
  readonly avgStarRatingDisplay = computed<string | null>(() => {
    const v = this.avgStarRating();
    return v === null ? null : v.toFixed(1);
  });
  readonly turnoverCompletionRate = computed<number | null>(
    () => this.effectiveOpsKpis()?.turnover_completion_rate ?? null,
  );
  readonly turnoverCompletionDisplay = computed<string | null>(() => {
    const v = this.turnoverCompletionRate();
    return v === null ? null : `${Math.round(v)}%`;
  });
  readonly guestResponseTimeMinutes = computed<number | null>(
    () => this.effectiveOpsKpis()?.account_guest_response_time_minutes ?? null,
  );
  readonly guestResponseDisplay = computed<string | null>(() => formatResponseTime(this.guestResponseTimeMinutes()));
  readonly proResponseTimeMinutes = computed<number | null>(
    () => this.effectiveOpsKpis()?.account_homekeeper_response_time_minutes ?? null,
  );
  readonly proResponseDisplay = computed<string | null>(() => formatResponseTime(this.proResponseTimeMinutes()));
  // Null too while the customer answers their own inquiries.
  readonly inquiryResponseTimeMinutes = computed<number | null>(
    () => this.effectiveOpsKpis()?.inquiry_response_time_minutes ?? null,
  );
  readonly inquiryResponseDisplay = computed<string | null>(() =>
    formatResponseTime(this.inquiryResponseTimeMinutes()),
  );

  // ---- Trends chart -----------------------------------------------------
  readonly trendMonths = computed<FinanceTrendsMonth[]>(() => this.effectiveTrends()?.months ?? []);
  readonly chartCategories = computed(() =>
    this.trendMonths().map(m => this.monthsShort[m.month - 1]),
  );
  readonly revenueActualSeries = computed(() =>
    this.trendMonths().map(m => this.toDollars(m.actual_revenue)),
  );
  readonly revenueProjectedSeries = computed(() =>
    this.trendMonths().map(m => this.toDollars(m.projected_revenue)),
  );
  readonly netActualSeries = computed(() =>
    this.trendMonths().map(m => this.toDollars(m.actual_net_income)),
  );
  readonly netProjectedSeries = computed(() =>
    this.trendMonths().map(m => this.toDollars(m.projected_net_income)),
  );

  readonly chartConfig = computed(() => {
    const categories = this.chartCategories();
    const fmt = (v: number | null | undefined) => v === null || v === undefined ? '—' : this.formatCurrency(v);

    // Scale each visible axis to BOTH its actual and projected series so the
    // labels match the bars/line. Without this, the visible Revenue / Net Income
    // axes bind to the actual series only — which collapses to $0–$2 when there
    // are no actuals (e.g. the Next-12-Months view), while the projected bars
    // render against their own hidden axes. `undefined` = let ApexCharts auto-scale.
    const revVals = [...this.revenueActualSeries(), ...this.revenueProjectedSeries()];
    const netVals = [...this.netActualSeries(), ...this.netProjectedSeries()];
    const revPeak = Math.max(0, ...revVals);
    const netPeak = Math.max(0, ...netVals);
    const netTrough = Math.min(0, ...netVals);
    // Constrain only when there's data; otherwise leave undefined for auto-scale.
    // Net income anchors at 0 and extends to cover any losses.
    const revMax = revPeak > 0 ? revPeak : undefined;
    const netHasData = netPeak > 0 || netTrough < 0;
    const netMax = netHasData ? netPeak : undefined;
    const netMin = netHasData ? netTrough : undefined;
    const root = typeof getComputedStyle === 'function' && typeof document !== 'undefined'
      ? getComputedStyle(document.documentElement)
      : null;
    const axisColor = root?.getPropertyValue('--tidy-text-muted')?.trim() || 'rgba(127, 127, 127, 0.7)';
    const axisTitleColor = root?.getPropertyValue('--tidy-text-light')?.trim() || 'rgba(127, 127, 127, 0.5)';
    const gridColor = root?.getPropertyValue('--tidy-border-light')?.trim() || 'rgba(127, 127, 127, 0.1)';
    return {
      series: [
        { name: 'Actual Revenue', type: 'column', data: this.revenueActualSeries() },
        { name: 'Projected Revenue', type: 'column', data: this.revenueProjectedSeries() },
        { name: 'Net Income (Actual)', type: 'line', data: this.netActualSeries() },
        { name: 'Net Income (Projected)', type: 'line', data: this.netProjectedSeries() },
      ],
      chart: {
        type: 'line',
        // 100% height so the plot grows to fill the chart card, which is
        // stretched by the grid to match the (taller) Suggestions card. The
        // `.trend-chart` host carries a flex height + min-height fallback.
        height: '100%',
        // Explicit 100% width so ApexCharts fills the card edge-to-edge.
        // Without this, lazy init can measure the parent before it has
        // its final width and render a narrow plot.
        width: '100%',
        toolbar: { show: false },
        animations: { enabled: false },
        background: 'transparent',
        stacked: false,
      },
      colors: ['#00AABA', '#A5F3FC', '#F59E0B', axisColor],
      stroke: { width: [0, 0, 3, 2.5], dashArray: [0, 0, 0, 6], curve: 'monotoneCubic' },
      plotOptions: {
        bar: { columnWidth: '55%', borderRadius: 3, dataLabels: { position: 'top' } },
      },
      fill: { opacity: [0.95, 0.6, 1, 1] },
      markers: { size: [0, 0, 4, 0], strokeWidth: 0 },
      grid: { borderColor: gridColor, padding: { left: 4, right: 8, top: 0, bottom: 0 } },
      xaxis: {
        categories,
        labels: { style: { colors: axisColor, fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          seriesName: 'Actual Revenue',
          min: 0,
          max: revMax,
          forceNiceScale: true,
          labels: {
            style: { colors: axisColor, fontSize: '11px' },
            formatter: (v: number) => this.formatCompact(v),
          },
          axisBorder: { show: false },
          title: { text: 'Revenue', style: { color: axisTitleColor, fontSize: '11px', fontWeight: 500 } },
        },
        // Projected revenue renders on its own (hidden) axis — give it the same
        // scale as the visible Revenue axis so its bars line up with the labels.
        { seriesName: 'Projected Revenue', show: false, min: 0, max: revMax, forceNiceScale: true },
        {
          opposite: true,
          seriesName: 'Net Income (Actual)',
          min: netMin,
          max: netMax,
          forceNiceScale: true,
          labels: {
            style: { colors: axisColor, fontSize: '11px' },
            formatter: (v: number) => this.formatCompact(v),
          },
          axisBorder: { show: false },
          title: { text: 'Net Income', style: { color: axisTitleColor, fontSize: '11px', fontWeight: 500 } },
        },
        // Same scale as the visible Net Income axis.
        { show: false, seriesName: 'Net Income (Projected)', min: netMin, max: netMax, forceNiceScale: true },
      ],
      legend: { show: false },
      tooltip: { theme: 'dark', shared: true, intersect: false, y: { formatter: fmt } },
    };
  });

  // ---- P&L Summary table ------------------------------------------------
  readonly summaryRows = computed<FinanceSummaryRow[]>(() => this.effectiveSummary()?.rows ?? []);
  readonly netIncomeRow = computed<FinanceSummaryRow | null>(
    () => this.summaryRows().find(r => r.subcategory === 'net_income') ?? null,
  );
  /** Everything except the synthetic net_income row, in the order BE returned them. */
  readonly summaryDetailRows = computed<FinanceSummaryRow[]>(
    () => this.summaryRows().filter(r => r.subcategory !== 'net_income'),
  );

  // ---- Formatters -------------------------------------------------------
  formatCurrency(v: number): string {
    const sign = v < 0 ? '-' : '';
    return `${sign}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
  }
  formatCompact(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) {return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;}
    if (abs >= 10_000) {return `${sign}$${Math.round(abs / 1000)}k`;}
    if (abs >= 1_000) {return `${sign}$${(abs / 1000).toFixed(1)}k`;}
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  }
  formatSignedCompact(v: number): string {
    if (Math.round(v) === 0) {return '$0';}
    const sign = v > 0 ? '+' : '-';
    const abs = Math.abs(v);
    if (abs >= 10_000) {return `${sign}$${Math.round(abs / 1000)}k`;}
    if (abs >= 1000) {return `${sign}$${(abs / 1000).toFixed(1)}k`;}
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  }
  formatVariancePct(v: number | null): string {
    if (v === null) {return '—';}
    const sign = v > 0 ? '+' : '';
    return `${sign}${v.toFixed(1)}%`;
  }
  varianceClass(row: FinanceSummaryRow): 'positive' | 'negative' | 'neutral' {
    if (Math.round(row.variance) === 0) {return 'neutral';}
    // Income subcategories want actual > projected; expense subcategories want actual < projected.
    // BE returns negative amounts for expenses, so "better" = variance > 0 in both cases.
    return row.variance > 0 ? 'positive' : 'negative';
  }
  /** Render expense rows as the absolute dollar amount (BE stores them negative). */
  rowDisplayAmount(cents: number): number {
    return Math.abs(this.toDollars(cents));
  }
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
